<template>
  <ion-header>
    <ion-toolbar :style="contentStyles">
      <ion-buttons slot="start">
        <ion-back-button v-if="!fiscalReceiptInProgress" />
      </ion-buttons>
      <ion-title>{{ $t('order.payment') }}</ion-title>
      <!-- Add Cash / Add Card stay reachable while the footer hosts the
           single-transaction's actions. Tap to split-pay an existing tx. -->
      <ion-buttons v-if="singleTxActionMode" slot="end">
        <ion-button
          :disabled="loadingCreateTransaction !== null || fiscalReceiptInProgress"
          @click="onCreateTransaction('cash')"
        >
          <AppIcon
            icon="wallet"
            class="w-6 h-6"
            stroke-width="2"
            color="text-fg-blue"
          />
        </ion-button>
        <ion-button
          :disabled="loadingCreateTransaction !== null || fiscalReceiptInProgress"
          @click="onCreateTransaction('card')"
        >
          <AppIcon
            icon="credit-card"
            class="w-6 h-6"
            stroke-width="2"
            color="text-fg-blue"
          />
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content :fullscreen="true" :style="contentStyles">
    <AppStack
      v-if="loading"
      direction="flex-col"
      flex="flex-1"
      align-items="items-center"
      justify-content="justify-center"
      class="py-10 w-full absolute h-full left-0 top-0"
    >
      <AppSpinner
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    </AppStack>

    <template v-else>
      <!-- Total moved to the footer (same pattern as OrderModalHomeFooter
           on the order screen). -->
      <div class="pt-2" />

      <!-- Transactions list -->
      <AppStack direction="flex-col">
        <AppStack class="px-5 pt-2 pb-1">
          <AppTypography
            :text="$t('order.transactions')"
            name="text-sm-4-medium"
            color="text-fg-elevation-2-secondary"
            class="uppercase"
          />
        </AppStack>
        <ion-list v-if="transactions.length" inset class="!my-2">
          <ion-item
            v-for="(transaction, index) in transactions"
            :key="transaction._id"
            :style="transactionItemStyles"
            :detail="false"
          >
            <AppStack
              direction="flex-col"
              class="py-3 gap-1 w-full"
              justify-content="justify-center"
            >
              <AppStack
                justify-content="justify-between"
                align-items="items-baseline"
                class="gap-3"
              >
                <AppTypography
                  :text="`${$t('transaction.label', { number: index + 1 })} · ${$t(`payment.methods.${transaction.type}`)}`"
                  name="text-md-4-medium"
                  :color="
                    transaction.reversed
                      ? 'text-fg-elevation-3-secondary'
                      : 'text-fg-elevation-3-primary'
                  "
                />
                <AppTypography
                  :text="formatPrice(transaction.amount)"
                  name="text-md-4-semibold"
                  :color="
                    transaction.reversed
                      ? 'text-fg-elevation-3-secondary'
                      : 'text-fg-elevation-3-primary'
                  "
                />
              </AppStack>
              <AppStack
                justify-content="justify-between"
                align-items="items-center"
                class="gap-3"
              >
                <AppLabel :variant="transactionLabelVariant(transaction)">
                  {{ transactionStatusLabel(transaction) }}
                </AppLabel>
                <AppStack align-items="items-center" class="gap-3">
                  <AppSpinner
                    v-if="fiscalReceiptInProgress"
                    class="!w-4 !h-4"
                  />
                  <!-- Inline actions only when 2+ transactions — single-tx
                       keeps actions in the sticky footer. Primary action
                       (Print Receipt) is inline; rest (reverse/delete/pre-
                       check) live in the action sheet behind the ⋮ button. -->
                  <template v-if="multiTxMode">
                    <button
                      v-if="canPrint(transaction)"
                      type="button"
                      class="active:opacity-50 px-1"
                      :disabled="fiscalReceiptInProgress"
                      @click="onTransactionPrint(transaction._id)"
                    >
                      <AppTypography
                        :text="$t('transaction.printReceipt')"
                        name="text-sm-5-semibold"
                        color="text-fg-blue"
                      />
                    </button>
                    <button
                      v-if="hasMoreActions(transaction)"
                      type="button"
                      class="active:opacity-50 px-1"
                      @click="onTransactionTap(transaction)"
                    >
                      <AppIcon
                        icon="more-horiz"
                        class="w-5 h-5"
                        stroke-width="2"
                        color="text-fg-elevation-3-secondary"
                      />
                    </button>
                  </template>
                </AppStack>
              </AppStack>
            </AppStack>
          </ion-item>
        </ion-list>
        <AppStack
          v-else
          align-items="items-center"
          justify-content="justify-center"
          class="px-5 py-6"
        >
          <AppTypography
            :text="$t('order.noTransactionsYet')"
            name="text-md-4-medium"
            color="text-fg-elevation-2-secondary"
          />
        </AppStack>
      </AppStack>
    </template>

    <ion-action-sheet
      :is-open="!!actionSheetTransactionId"
      :buttons="actionSheetButtons"
      @did-dismiss="actionSheetTransactionId = ''"
    />
  </ion-content>

  <!-- Footer pinned to viewport bottom by Ionic regardless of content height.
       Two modes:
       - !allPaid + possibleToCreateTransaction → Add Cash + Add Card
       - allPaid → change display + numpad + Finalise (cash-change calc)
       Same paddings + gradient-fade pattern as OrderModalHomeOrder's footer:
       no hard border, the last row of the list dissolves under it. -->
  <ion-footer v-if="!loading" :style="contentStyles">
    <div class="relative w-full safe-bottom-max bg-bg-elevation-2">
      <div
        class="absolute top-[-90px] left-0 w-full h-[90px] pointer-events-none bg-gradient-to-b
          to-bg-elevation-2 from-transparent"
      />

      <!-- Active fiscal-receipt banner. Server is talking to the
           printer right now. Operator sees a clear "in progress"
           state so the locked buttons don't read as broken. -->
      <div
        v-if="fiscalReceiptInProgress"
        class="px-4 pt-3 pb-1 flex items-center gap-2 text-sm font-medium"
        :style="{ color: 'var(--rf-fg-elevation-2-primary)' }"
      >
        <ion-spinner name="dots" class="w-5 h-5" />
        <span>{{ $t('transaction.printingReceipt') }}</span>
      </div>

      <!-- Total — matches the order screen's footer pattern: big heading
           on the left, paid-so-far indicator on the right when partial. -->
      <AppStack
        align-items="items-baseline"
        justify-content="justify-between"
        class="px-4 pb-3 gap-2"
      >
        <AppTypography
          :text="formatPrice(orderTotal)"
          name="heading-2xl-8-bold"
          color="text-fg-elevation-2-primary"
        />
        <AppTypography
          v-if="paidTotal > 0 && paidTotal < orderTotal"
          :text="`${formatPrice(paidTotal)} ${$t('order.paid')}`"
          name="text-md-4-medium"
          color="text-fg-elevation-2-secondary"
        />
      </AppStack>

      <!-- Single-transaction case — actions live in the footer; Add Cash/
           Card are relegated to the toolbar end-slot for split payments. -->
      <AppStack
        v-if="singleTxActionMode && firstTx"
        direction="flex-col"
        class="px-4 gap-2"
      >
        <AppButton
          v-if="canPreCheck(firstTx)"
          :variant="
            preCheckedTxIds.has(firstTx._id) ? 'outline-secondary' : 'primary'
          "
          :text="$t('transaction.printPreCheck')"
          @click="onTransactionPreCheckPrint(firstTx._id)"
        />
        <AppButton
          v-if="canPrint(firstTx)"
          :loading="fiscalReceiptInProgress"
          :disabled="fiscalReceiptInProgress"
          :variant="
            preCheckedTxIds.has(firstTx._id) ? 'primary' : 'outline-secondary'
          "
          :text="$t('transaction.printReceipt')"
          @click="onTransactionPrint(firstTx._id)"
        />
        <AppButton
          v-if="hasMoreActions(firstTx)"
          :disabled="fiscalReceiptInProgress"
          variant="outline-secondary"
          :text="$t('common.more')"
          @click="onTransactionTap(firstTx)"
        />
      </AppStack>

      <AppStack
        v-else-if="!allTransactionsArePaid && possibleToCreateTransaction"
        direction="flex-col"
        class="px-4 gap-2"
      >
        <AppButton
          :loading="loadingCreateTransaction === 'cash'"
          :disabled="loadingCreateTransaction !== null || fiscalReceiptInProgress"
          variant="secondary-blue"
          @click="onCreateTransaction('cash')"
        >
          <AppStack
            align-items="items-center"
            justify-content="justify-center"
            class="relative gap-2"
          >
            <AppIcon
              icon="wallet"
              size="custom"
              class="w-5 h-5"
              stroke-width="1.8px"
              color="text-fg-blue"
            />
            <AppTypography
              :text="$t('transaction.addCash')"
              name="text-base-6-semibold"
              color="text-fg-blue"
            />
          </AppStack>
        </AppButton>
        <AppButton
          :loading="loadingCreateTransaction === 'card'"
          :disabled="loadingCreateTransaction !== null || fiscalReceiptInProgress"
          variant="primary"
          @click="onCreateTransaction('card')"
        >
          <AppStack
            align-items="items-center"
            justify-content="justify-center"
            class="relative gap-2"
          >
            <AppIcon
              icon="credit-card"
              size="custom"
              class="w-5 h-5"
              stroke-width="1.8px"
              color="text-universal-fg-opposite"
            />
            <AppTypography
              :text="$t('transaction.addCard')"
              name="text-base-6-semibold"
              color="text-universal-fg-opposite"
            />
          </AppStack>
        </AppButton>
      </AppStack>

      <AppStack
        v-else-if="
          allTransactionsArePaid && order?.status?.name !== 'checked-out'
        "
        direction="flex-col"
        align-items="items-center"
        class="px-4 gap-4"
      >
        <AppStack
          class="px-5 py-3 bg-bg-elevation-2-breaker gap-2 rounded-lg w-full"
          justify-content="justify-between"
          align-items="items-baseline"
        >
          <AppTypography
            :text="formatPrice(changeTotal, false)"
            name="text-md-4-semibold"
            color="text-fg-elevation-2-secondary"
          />
          <AppTypography
            :text="formatPrice(Number(customTotal))"
            name="heading-2xl-8-bold"
            color="text-fg-elevation-2-primary"
          />
        </AppStack>
        <AppCheckoutNumpad @key="onNumpadKey" @delete="onNumpadDelete" />
        <AppButton
          :loading="finaliseInProgress"
          :disabled="finaliseInProgress || fiscalReceiptInProgress"
          :text="$t('order.finalise')"
          variant="primary"
          class="w-full"
          @click="onFinalise"
        />
      </AppStack>
    </div>
  </ion-footer>
</template>

<script lang="ts">
export default { name: 'OrderModalHomeCheckout' }
</script>

<script setup lang="ts">
import {
  ref,
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  watch,
  type ComputedRef,
} from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonList,
  IonItem,
  IonActionSheet,
  IonSpinner,
  toastController,
} from '@ionic/vue'
import Bugsnag from '@bugsnag/js'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import AppSpinner from '@restify/packages/design-system/low-level/AppSpinner.vue'
import AppLabel from '@restify/packages/design-system/low-level/AppLabel.vue'
import AppCheckoutNumpad from '@restify/packages/design-system/mid-level/AppCheckoutNumpad.vue'
import AppButton from '~/components/AppButton.vue'
import {
  useFiscalStore,
  getReceiptTotal,
  getReceiptPaymentsTotal,
} from '@restify/packages/stores/fiscal'
import { roundValue } from '@restify/packages/helpers/round'
import useFeathers from '~/composables/useFeathers'
import useStores, { type Stores } from '~/composables/useStores'
import useAppHelpers from '~/composables/useAppHelpers'
import useAppEventBus from '~/composables/useAppEventBus'
import { useAuthStore } from '~/stores/auth'

const { t } = useI18n()
const ApiClient = useFeathers()
const FiscalStore = useFiscalStore()
const AuthStore = useAuthStore()
const appBus = useAppEventBus()
const {
  transactions: TransactionsStore,
  orders: OrdersStore,
  apps: AppsStore,
} = useStores()
const { formatPrice } = useAppHelpers()

const injectedOrderId = inject<ComputedRef<string>>('orderId')

const loading = ref(true)
const loadingCreateTransaction = ref<null | 'cash' | 'card'>(null)
const finaliseInProgress = ref(false)
// True while the atomic fiscal-receipt POST is in flight. Drives the
// UI lock — Finalize button, transaction action buttons and the
// parent sheet's can-dismiss all gate on this so the operator can't
// race the server-side flow with concurrent actions.
const fiscalReceiptInProgress = ref(false)
const actionSheetTransactionId = ref('')
// Transactions whose customer pre-check has been printed this session.
// Drives button emphasis: pre-check is primary until printed, then the
// fiscal receipt becomes primary.
const preCheckedTxIds = ref<Set<string>>(new Set())

// Cash-change numpad — only visible on the Finalise step. Three slots
// for digits build a "1234" → 12.34 amount, same UX as station-app's
// OrderModalContentFinalise.
const customTotalDigits = ref<Array<null | number>>([null, null, null])

const isManager = computed(() => {
  const t = AuthStore.reactiveUser?.permissions?.type
  return t === 'superadmin' || t === 'manager' || t === 'app-owner'
})

const contentStyles = computed(() => ({
  '--background': 'var(--rf-bg-elevation-2)',
  '--border-color': 'var(--rf-border-elevation-2-primary)',
}))

// ion-list inset puts the items on a "card" surface. The sheet/modal
// itself already sits on elevation-2, so use elevation-3 for the items
// to stand out instead of disappearing into the same surface.
const transactionItemStyles = {
  '--background': 'var(--rf-bg-elevation-3)',
  '--background-activated': 'var(--rf-bg-elevation-3-alt)',
  '--background-activated-opacity': '0.5',
  '--border-color': 'var(--rf-border-elevation-3-secondary)',
  '--padding-start': '16px',
  '--inner-padding-end': '16px',
}

const order = computed(() => {
  if (!injectedOrderId?.value) return null

  return OrdersStore.getFromStore(injectedOrderId.value).value as
    | Stores['orders']['Result']
    | null
})

const transactions = computed(() => {
  if (!order.value?._id) return [] as Stores['transactions']['Result'][]

  return TransactionsStore.findInStore({
    orderId: order.value._id,
    $sort: { createdAt: 1 },
  })
    .map((item) => item.value)
    .filter((item): item is Stores['transactions']['Result'] => !!item)
})

const firstTx = computed(() => transactions.value[0] ?? null)
const multiTxMode = computed(() => transactions.value.length >= 2)

// Per-transaction availability — used to drive both inline buttons and
// the overflow action sheet, so the two surfaces stay consistent.
const canPrint = (tx: Stores['transactions']['Result']) =>
  !tx.paid && !tx.reversed && !!tx.items?.length

const canReverse = (tx: Stores['transactions']['Result']) =>
  tx.paid && !tx.reversed && tx.type !== 'card' && isManager.value

const canDelete = (tx: Stores['transactions']['Result']) =>
  !tx.paid && !tx.reversed

const canPreCheck = (tx: Stores['transactions']['Result']) =>
  !tx.paid &&
  !tx.reversed &&
  !!tx.items?.length &&
  order.value?.status?.name !== 'checked-out'

const hasMoreActions = (tx: Stores['transactions']['Result']) =>
  canPreCheck(tx) || canReverse(tx) || canDelete(tx)

// Single-transaction case: tx-specific actions go to the sticky footer,
// Add Cash/Card move to the toolbar end-slot. Multi-tx (≥2) puts actions
// inline per row and keeps Add Cash/Card in the footer.
const singleTxActionMode = computed(
  () =>
    transactions.value.length === 1 &&
    !allTransactionsArePaid.value &&
    order.value?.status?.name !== 'checked-out',
)

const orderTotal = computed(() => {
  return (
    (typeof order.value?.appliedPromotions?.total === 'number'
      ? order.value.appliedPromotions.total
      : order.value?.total) || 0
  )
})

const paidTotal = computed(() => {
  return transactions.value
    .filter((tx) => !tx.reversed && tx.paid)
    .reduce((acc, tx) => roundValue(acc + (tx.amount || 0), 2), 0)
})

const inProgressTotal = computed(() => {
  return transactions.value
    .filter((tx) => !tx.reversed)
    .reduce((acc, tx) => roundValue(acc + (tx.amount || 0), 2), 0)
})

const allTransactionsArePaid = computed(() => {
  return (
    paidTotal.value >= orderTotal.value &&
    transactions.value.length > 0 &&
    transactions.value.every((tx) => tx.paid)
  )
})

const possibleToCreateTransaction = computed(() => {
  return inProgressTotal.value < orderTotal.value || orderTotal.value === 0
})

// Sale items not yet covered by a paid transaction — server-side
// transaction creation needs the keys, dev-mode synth needs the full
// price/discount payload. Same logic as station-app.
const saleItems = computed(() => {
  const paidTransactionItems = transactions.value
    .filter((tx) => !tx.reversed && tx.paid)
    .reduce<Record<string, any>>((acc, tx) => {
      tx.items.forEach((item: any) => {
        acc[item.id] = item
      })

      return acc
    }, {})

  return order.value?.menuItems.reduce<Record<string, any>>((acc, menuItem) => {
    if (paidTransactionItems[menuItem._id]) return acc

    if (order.value?.appliedPromotions?.menuItems?.[menuItem._id]) {
      acc[menuItem._id] = {
        ...order.value.appliedPromotions.menuItems[menuItem._id],
      }

      return acc
    }
    acc[menuItem._id] = {
      id: menuItem._id,
      menuItemId: menuItem.menuItemId,
      price: menuItem.price,
      quantity: menuItem.quantity,
      amountBeforeDiscount: menuItem.price,
      amountAfterDiscount: menuItem.price,
      discount: 0,
    }

    return acc
  }, {})
})

// Cash-change numpad amount, parsed back to a number with cents.
const customTotal = computed(() => {
  return customTotalDigits.value.reduce<string>((acc, number, index) => {
    const n = number === null ? 0 : number
    const arr = customTotalDigits.value

    if (arr.length > 2 && arr.length - index === 2) {
      acc += '.'
    }

    acc += n

    return acc
  }, '')
})

const changeTotal = computed(() => Number(customTotal.value) - orderTotal.value)

watch(
  orderTotal,
  (newVal) => {
    customTotalDigits.value = newVal
      .toFixed(2)
      .replace('.', '')
      .split('')
      .map((str) => parseInt(str, 10))
  },
  { immediate: true },
)

const onNumpadKey = (key: number) => {
  if (Number(customTotal.value).toFixed(2) === orderTotal.value.toFixed(2)) {
    onNumpadDelete()
  }
  const nullIndex = customTotalDigits.value.lastIndexOf(null)
  if (nullIndex > -1) {
    customTotalDigits.value.splice(nullIndex, 1)
    customTotalDigits.value.push(key)

    return
  }
  customTotalDigits.value.push(key)
}

const onNumpadDelete = () => {
  customTotalDigits.value = [null, null, null]
}

const transactionStatusLabel = (tx: Stores['transactions']['Result']) => {
  if (tx.reversed) return t('transaction.reversed')
  if (tx.paid) return t('transaction.paid')

  return t('transaction.pending')
}

const transactionLabelVariant = (
  tx: Stores['transactions']['Result'],
): 'fill-gray-faded' | 'fill-green-faded' | 'fill-orange-faded' => {
  if (tx.reversed) return 'fill-gray-faded'
  if (tx.paid) return 'fill-green-faded'

  return 'fill-orange-faded'
}

const actionSheetButtons = computed(() => {
  if (!actionSheetTransactionId.value) return []
  const tx = transactions.value.find(
    (t) => t._id === actionSheetTransactionId.value,
  )
  if (!tx) return []

  const buttons: { text: string; role?: string; handler?: () => void }[] = []
  const closeAndRun = (fn: () => void) => () => {
    actionSheetTransactionId.value = ''
    fn()
  }

  // Reversed transactions only show Cancel.
  if (tx.reversed) {
    buttons.push({ text: t('common.cancel'), role: 'cancel' })

    return buttons
  }

  // Customer pre-check (the bill) — always available pre-checkout for any
  // non-paid tx with items. Independent of fiscal flow.
  if (
    !tx.paid &&
    tx.items?.length &&
    order.value?.status?.name !== 'checked-out'
  ) {
    buttons.push({
      text: t('transaction.printPreCheck'),
      handler: closeAndRun(() => onTransactionPreCheckPrint(tx._id)),
    })
  }
  if (!tx.paid) {
    buttons.push({
      text: t('transaction.printReceipt'),
      handler: closeAndRun(() => onTransactionPrint(tx._id)),
    })
  }
  // Reverse — manager-only, paid + non-card + non-reversed. Cash-paid
  // (or other non-card) only because reversing a card payment would also
  // need a corresponding card refund flow we don't have wired yet.
  if (tx.paid && !tx.reversed && tx.type !== 'card' && isManager.value) {
    buttons.push({
      text: t('transaction.reverse'),
      role: 'destructive',
      handler: closeAndRun(() => onTransactionReverse(tx._id)),
    })
  }
  if (!tx.paid) {
    buttons.push({
      text: t('transaction.deleteTransaction'),
      role: 'destructive',
      handler: closeAndRun(() => onTransactionDelete(tx._id)),
    })
  }
  buttons.push({ text: t('common.cancel'), role: 'cancel' })

  return buttons
})

const onTransactionTap = (tx: Stores['transactions']['Result']) => {
  actionSheetTransactionId.value = tx._id
}

const onCreateTransaction = async (type: 'cash' | 'card') => {
  if (!order.value?._id) return

  // Always go through the real checkout service — the server creates the
  // transaction row, broadcasts 'transactions created' via socket, the
  // TransactionsStore listener picks it up, and the computed re-renders.
  // Dev mode only matters at the print step.
  if (!FiscalStore.printerId || !FiscalStore.printerStatus.ok) {
    displayError(t('transaction.printerNotConfigured'))

    return
  }
  loadingCreateTransaction.value = type
  try {
    await ApiClient.service('checkout').create({
      orderId: order.value._id,
      action: 'create-transaction',
      printerId: FiscalStore.printerId,
      type,
      saleItems: Object.keys(saleItems.value || {}),
    })
  } catch (error: any) {
    displayError(error?.message || 'Failed to create transaction')
  } finally {
    loadingCreateTransaction.value = null
  }
}

// Atomic fiscal-receipt flow: one server call, single await, full
// idempotency on the server side. Replaces the old 4-call dance
// (POST receipt → client→Pi print → client→Pi getTask → PATCH receipt
// via watcher) that raced the Finalize tap. The server now does all
// of that internally and returns the updated transaction.
//
// See docs/plans/2026-06-30-fiscal-receipt-refactor.md for the design
// and the race investigation that motivated the rewrite.
const onTransactionPrint = async (transactionId: string) => {
  if (!order.value?._id) return
  if (fiscalReceiptInProgress.value) return

  fiscalReceiptInProgress.value = true
  try {
    await ApiClient.service('checkout').create({
      action: 'fiscal-receipt',
      transactionId,
    })
    // tx.paid + receiptNumber lands via the realtime patched event;
    // the AppBadge above re-renders to "Paid" without us touching it.
  } catch (error: any) {
    Bugsnag.notify(
      error instanceof Error ? error : new Error(String(error)),
      (event) => {
        event.addMetadata('fiscal-receipt', {
          transactionId,
          orderId: order.value?._id,
          code: error?.data?.code,
          messages: error?.data?.messages,
          details: error?.data?.details,
        })
      },
    )
    const code = error?.data?.code
    const fallbackByCode: Record<string, string> = {
      'pi-unreachable': 'Cannot reach the printer. Check the printer/Wi-Fi.',
      'pi-call-failed': 'Printer call failed. Retry in a moment.',
      'pi-timeout': 'Printer did not respond in time. Verify and retry.',
      'pi-task-lost': 'Printer state was reset. Verify the receipt and retry.',
      'printer-failure': 'Printer reported a failure. Check the device.',
      'printer-missing': 'No printer configured for this app.',
      'tx-reversed': 'This transaction has already been reversed.',
      'tx-not-found': 'Transaction not found on the server.',
      'item-not-in-order':
        'A receipt item is no longer in the order (it was removed). Refresh the order and check the items before printing.',
    }
    displayError(
      fallbackByCode[code] ||
        error?.message ||
        'Failed to print receipt',
    )
  } finally {
    fiscalReceiptInProgress.value = false
  }
}

const onTransactionPreCheckPrint = async (transactionId: string) => {
  if (!order.value?._id) return
  const appId = (order.value as any).appId as string | undefined
  const app = appId ? AppsStore.getFromStore(appId).value : null
  const transaction = TransactionsStore.getFromStore(transactionId).value
  if (!app || !transaction) return

  try {
    await ApiClient.service('pi-printer').create({
      app,
      order: order.value,
      transaction,
    })
    // Mark printed so the fiscal receipt becomes the primary action.
    preCheckedTxIds.value = new Set(preCheckedTxIds.value).add(transactionId)
  } catch (error: any) {
    displayError(error?.message || 'Pre-check print failed')
  }
}

// Atomic reversal — symmetric to onTransactionPrint. One server call,
// server walks all five idempotency layers (tx state → Pi state →
// in-flight wait → ErpNet taskId lock → Mongo atomic patch), returns
// the reversed transaction. The realtime patched event flows the
// reversed:true + reversal receipt metadata into the local store and
// the UI re-renders.
const onTransactionReverse = async (transactionId: string) => {
  if (!order.value?._id) return
  if (fiscalReceiptInProgress.value) return

  fiscalReceiptInProgress.value = true
  try {
    await ApiClient.service('checkout').create({
      action: 'fiscal-reversal-receipt',
      transactionId,
    })
  } catch (error: any) {
    Bugsnag.notify(
      error instanceof Error ? error : new Error(String(error)),
      (event) => {
        event.addMetadata('fiscal-reversal-receipt', {
          transactionId,
          orderId: order.value?._id,
          code: error?.data?.code,
          messages: error?.data?.messages,
        })
      },
    )
    const code = error?.data?.code
    const fallbackByCode: Record<string, string> = {
      'pi-unreachable': 'Cannot reach the printer. Check the printer/Wi-Fi.',
      'pi-call-failed': 'Printer call failed. Retry in a moment.',
      'pi-timeout': 'Printer did not respond in time. Verify and retry.',
      'pi-task-lost': 'Printer state was reset. Verify the receipt and retry.',
      'printer-failure': 'Printer reported a failure. Check the device.',
      'printer-missing': 'No printer configured for this app.',
      'tx-not-paid': 'This transaction is not paid — nothing to reverse.',
      'tx-not-found': 'Transaction not found on the server.',
    }
    displayError(
      fallbackByCode[code] || error?.message || 'Reverse failed',
    )
  } finally {
    fiscalReceiptInProgress.value = false
  }
}

const onTransactionDelete = async (transactionId: string) => {
  if (!order.value?._id) return

  try {
    await ApiClient.service('checkout').create({
      action: 'remove-transaction',
      orderId: order.value._id,
      transactionId,
    })
  } catch (error: any) {
    displayError(error?.message || 'Delete failed')
  }
}

const onFinalise = async () => {
  const orderId = order.value?._id
  if (!orderId) {
    // Bugsnag breadcrumb so the next "Order not found" report tells
    // us whether the id was missing client-side or rejected server-
    // side. Silent return keeps current UX — operator just won't see
    // anything happen, no toast.
    Bugsnag.notify(
      new Error('Finalise aborted: order._id missing at click time'),
      (event) => {
        event.severity = 'warning'
        event.addMetadata('finalize', {
          injectedOrderId: injectedOrderId?.value ?? null,
          orderInStore: !!order.value,
        })
      },
    )

    return
  }

  finaliseInProgress.value = true
  try {
    await ApiClient.service('checkout').create({
      action: 'checkout',
      orderId,
    })
    // Close the whole order modal (not just pop the checkout sheet
    // inside it) — the order is finalised, the operator is done.
    // The same event bus is used elsewhere in the codebase to
    // dismiss the parent ion-modal in OrderModalLayout. Failures
    // keep everything open so the toast is visible and the
    // operator can retry without losing context.
    appBus.emit('order-modal-dismiss')
  } catch (error: any) {
    // Log to Bugsnag with full context — "No order found suitable
    // for checkout" surfaces sporadically and we still don't know
    // why. Capturing the exact orderId and order snapshot at fail
    // time should narrow it down on the next occurrence.
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('finalize', {
        orderId,
        orderStatus: order.value?.status?.name,
        orderAppId: (order.value as any)?.appId,
        orderStorefrontId: (order.value as any)?.storefrontId,
        // Strong suspect for the "Order not found" toast: JWT storefront
        // scoped to one storefront while the order was created under
        // another. scopeToStorefrontId on the server would then 404 the
        // get() inside checkout. Confirm by comparing these two values
        // on the next occurrence.
        authStorefrontId: AuthStore.parsedAccessToken?.storefrontId,
        authAppId: AuthStore.parsedAccessToken?.appId,
        message: error?.message,
      })
    })
    displayError(error?.message || 'Failed to finalise checkout')
  } finally {
    finaliseInProgress.value = false
  }
}

const displayError = async (message: string) => {
  const toast = await toastController.create({
    message,
    duration: 3000,
    position: 'bottom',
    cssClass: 'toast text-lg-7-semibold',
    mode: 'ios',
  })
  toast.present()
}

const init = async () => {
  if (!injectedOrderId?.value) return
  await Promise.all([
    OrdersStore.get(injectedOrderId.value),
    TransactionsStore.find({ query: { orderId: injectedOrderId.value } }),
  ]).catch((error) => console.error('Checkout init failed:', error))
  loading.value = false
  // Fire-and-forget — the loader shouldn't block on the printer round-trip
  // (real ErpNet polls can take up to its 30s timeout when the printer is
  // unreachable). printerStatus updates the UI on its own when it lands.
  FiscalStore.updatePrinterStatus()
}

onMounted(() => {
  init()
})
</script>
